import { useDebounceFn, useMemoizedFn } from 'ahooks';
import { Radio } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxMeasureNumberInput } from 'components/RevxMeasureInput/RevxMeasureNumberInput';
import { MeasureType } from 'domains/measurementDomain';
import { useMeasurementUnit } from 'hooks/useMeasurementUnit';
import { useUser } from 'hooks/useUser';
import { useEffect, useMemo, useRef, useState } from 'react';

/** The default bleed, in both units - an eighth of an inch. */
const DEFAULT_BLEED_IN = 0.125;
const DEFAULT_BLEED_MM = 3.18;

export const SkuBleedSize = () => {
  const curItem = useAppSelector((state) => state.sku.curItem);
  const skuTesterVisible = useAppSelector(
    (state) => state.sku.skuTesterState.visible
  );
  const orgList = useAppSelector((state) => state.org.list);
  const user = useUser();
  const dispatch = useAppDispatch();
  const curOrg = useMemo(() => {
    const curOrgId = localStorage.getItem('org') || user?.orgId;
    return orgList.find((org) => org.id === Number(curOrgId));
  }, [orgList, user]);
  const { abbrev, unit, metric } = useMeasurementUnit();
  const [bleedSize, setBleedSize] = useState<number>();
  const [overrideBleedSize, setOverrideBleedSize] = useState<boolean>(false);
  /**
   * Whether the bleed itself was changed, as opposed to the radio beside it.
   *
   * This saves on a debounce, so toggling the radio alone posts the bleed back too. Read in
   * millimetres that is a round trip the value did not need - a stored 0.0625in is shown as
   * 1.588mm and comes back 0.062520in, a little worse every time. What is not mentioned is left
   * alone by the API, so an untouched bleed is simply not sent.
   */
  const bleedEdited = useRef(false);
  // Keyed on the unit as well as the org: the API sends both, and switching units refetches
  // nothing, so without this the value stays in the unit it loaded in while the label flips.
  useEffect(() => {
    setBleedSize(metric ? curOrg?.bleedSizeMm ?? curOrg?.bleedSize : curOrg?.bleedSize);
    setOverrideBleedSize(Boolean(curOrg?.overrideBleedSize));
    // Refilled from the record, so whatever was typed before is no longer what is in the box.
    bleedEdited.current = false;
  }, [curOrg, metric]);
  const { run: debounceSubmit, cancel: cancelDebounceSubmit } = useDebounceFn(
    () => {
      submit();
    },
    { wait: 1500 }
  );
  const submit = useMemoizedFn(async () => {
    // The unit is stated only when a bleed is actually being submitted, because it describes
    // that number and nothing else in this payload. Declaring it over a spread of the stored
    // record is how an org's 0.125in bleed was once saved as 0.125mm.
    await dispatch.org.saveOrg({
      ...curOrg,
      overrideBleedSize,
      ...(bleedEdited.current
        ? { bleedSize, enteredUnit: unit }
        : // Untouched: left out entirely rather than round-tripped. See `bleedEdited`.
          { bleedSize: undefined }),
    });
  });
  return (
    <>
      <RevxFlexLayout
        style={{ marginBottom: '10px' }}
        hidden={!!curItem || skuTesterVisible}
      >
        <span style={{ paddingRight: '10px' }}>Override all bleed values:</span>
        <Radio.Group
          value={overrideBleedSize}
          onChange={(e) => {
            setOverrideBleedSize(e.target.value);
            if (e.target.value && !bleedSize) {
              // The house default is an eighth of an inch; offer it in whichever unit is shown.
              setBleedSize(metric ? DEFAULT_BLEED_MM : DEFAULT_BLEED_IN);
              // Chosen by turning the override on, so it does have to be saved.
              bleedEdited.current = true;
            }
            cancelDebounceSubmit();
            debounceSubmit();
          }}
        >
          <Radio value={true}>Yes</Radio>
          <Radio value={false}>No</Radio>
        </Radio.Group>
        <RevxFlexLayout hidden={!overrideBleedSize}>
          <span style={{ paddingRight: '10px' }}>New bleed value: </span>
          <RevxMeasureNumberInput
            type={MeasureType.LENGTH}
            value={bleedSize}
            onChange={(value) => {
              setBleedSize(value);
              bleedEdited.current = true;
              cancelDebounceSubmit();
              debounceSubmit();
            }}
            size="small"
            style={{ width: '80px', marginRight: '10px' }}
          />
          {/* Spelled, to match the suffix every other measurement field carries. */}
          <span>{abbrev(MeasureType.LENGTH)}</span>
        </RevxFlexLayout>
      </RevxFlexLayout>
    </>
  );
};
